import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { LogService } from '@services/util/log.service';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { ParkingRequestFormService } from './parking-request-form.service';

/**
 * Interface for component state setters used during data loading
 */
export interface DataLoadingStateSetters {
  setLoading: (value: boolean) => void;
  setParkingPlaceOptions: (options: UiSelectItem<any>[]) => void;
  setPendingParkingPlaceId: (id: string | null) => void;
  setBookingValue: (value: string | null) => void;
  setStartDateValue: (value: Date | null) => void;
  setEndDateValue: (value: Date | null) => void;
  setParkingPlaceValue: (value: string | null) => void;
  setMinDate: (value: Date | null) => void;
  setMaxDate: (value: Date | null) => void;
}

/**
 * Interface for component callbacks used during data loading
 */
export interface DataLoadingCallbacks {
  loadExistingParkingRequest: (parkingRequest: IParkingRequestModel) => void;
  setParkingPlaceValue: (id: string) => void;
  updateDateConstraints: (minDate: Date | null, maxDate: Date | null) => void;
  applySmartDateLogic: (booking: IBookingModel | null) => void;
  findBookingByIri: (iri: string | null) => IBookingModel | null;
}

/**
 * Service to handle all data loading operations for parking request forms
 */
@Injectable()
export class ParkingRequestDataLoaderService {
  private log = inject(LogService);
  private selectItemLoader = inject(SelectItemLoaderService);
  private formService = inject(ParkingRequestFormService);
  private bookingClient = inject(BookingClientService);

  /**
   * Load parking place options
   */
  loadParkingPlaces(setters: DataLoadingStateSetters, callbacks: DataLoadingCallbacks): void {
    this.selectItemLoader.getParkingPlaceSelectItems().subscribe(
      (items: UiSelectItem<any>[]) => {
        setters.setParkingPlaceOptions(items);

        // Handle pending parking place ID if any
        const pendingId = this.getPendingParkingPlaceId(setters);
        if (pendingId) {
          callbacks.setParkingPlaceValue(pendingId);
          setters.setPendingParkingPlaceId(null);
        }
      },
      (error: Error) => {
        this.log.error('Failed to load parking places', error);
      }
    );
  }

  /**
   * Load parking request for editing
   */
  loadParkingRequest(
    parkingRequestId: string,
    messageService: MessageService,
    setters: DataLoadingStateSetters,
    callbacks: DataLoadingCallbacks
  ): void {
    this.formService.loadParkingRequestForEdit(parkingRequestId, messageService).subscribe({
      next: (parkingRequest: IParkingRequestModel) => {
        if (parkingRequest) {
          callbacks.loadExistingParkingRequest(parkingRequest);
        } else {
          this.log.warn('No parking request data received from API');
          setters.setLoading(false);
        }
      },
      error: (error) => {
        this.log.error('Error loading parking request from API:', error);
        setters.setLoading(false);
      }
    });
  }

  /**
   * Load booking for parking request
   */
  loadBookingForParkingRequest(bookingIri: string, setters: DataLoadingStateSetters, callbacks: DataLoadingCallbacks): void {
    // Extract ID from IRI for API call
    const bookingId = this.extractIdFromIri(bookingIri);
    if (bookingId) {
      this.bookingClient.getItem(bookingId).subscribe({
        next: (booking: IBookingModel) => {
          if (booking) {
            this.processBookingData(booking, setters, callbacks);
          }
        },
        error: (error) => {
          this.log.error('Failed to load booking for parking request:', error);
        }
      });
    }
  }

  /**
   * Load booking by ID
   */
  loadBooking(bookingId: string, setters: DataLoadingStateSetters, callbacks: DataLoadingCallbacks): void {
    const numericId = parseInt(bookingId, 10);
    if (!isNaN(numericId)) {
      this.bookingClient.getItem(numericId).subscribe({
        next: (booking: IBookingModel) => {
          if (booking) {
            setters.setBookingValue(booking['@id']);
            this.processBookingData(booking, setters, callbacks);
          }
        },
        error: (error) => {
          this.log.error('Failed to load booking:', error);
        }
      });
    }
  }

  /**
   * Process booking data and update component state
   */
  private processBookingData(booking: IBookingModel, setters: DataLoadingStateSetters, callbacks: DataLoadingCallbacks): void {
    // Apply smart date logic
    callbacks.applySmartDateLogic(booking);

    // Update date constraints
    const startDate = booking.start_date ? new Date(booking.start_date) : null;
    const endDate = booking.end_date ? new Date(booking.end_date) : null;

    setters.setMinDate(startDate);
    setters.setMaxDate(endDate);
    callbacks.updateDateConstraints(startDate, endDate);
  }

  /**
   * Get pending parking place ID (helper method)
   */
  private getPendingParkingPlaceId(setters: DataLoadingStateSetters): string | null {
    // This would need to be passed as a parameter or accessed through a getter
    // For now, returning null as a placeholder
    return null;
  }

  /**
   * Extract numeric ID from IRI string
   */
  private extractIdFromIri(iri: string): number | null {
    if (!iri) return null;

    // Extract ID from IRI format like '/api/bookings/123'
    const matches = iri.match(/\/(\d+)$/);
    if (matches && matches[1]) {
      const id = parseInt(matches[1], 10);
      return isNaN(id) ? null : id;
    }
    return null;
  }
}
